import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import api from '../utils/api';

const Profile = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [userRes, bookingsRes, reviewsRes] = await Promise.all([
        api.get('/users/profile'),
        api.get('/users/bookings'),
        api.get('/users/reviews')
      ]);
      setUser(userRes.data);
      setFormData({
        name: userRes.data.name || '',
        email: userRes.data.email || '',
        phone: userRes.data.phone || ''
      });
      setBookings(bookingsRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [navigate, fetchData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await api.patch('/users/profile', formData);
      setUser(res.data);
      localStorage.setItem('user', JSON.stringify(res.data));
      setEditing(false);
    } catch (error) {
      setError(error.response?.data?.error || (language === 'en' ? 'Failed to update profile' : 'فشل تحديث الملف الشخصي'));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'en' ? 'en-US' : 'ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return timeString;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-blackC flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-silver">{t.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blackC pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-display text-4xl lg:text-5xl text-cream mb-4">
            {t.myAccount}
          </h1>
          <p className="text-gray-light">
            {language === 'en' ? 'Manage your account, bookings, and reviews' : 'إدارة حسابك وحجوزاتك وتقييماتك'}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-gray-dark">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'profile'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-light hover:text-cream'
              }`}
          >
            {t.accountInfo}
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'bookings'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-light hover:text-cream'
              }`}
          >
            {t.myBookings} ({bookings.length})
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'reviews'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-light hover:text-cream'
              }`}
          >
            {t.myReviews} ({reviews.length})
          </button>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-dark border border-gray-dark rounded-lg p-8">
            {error && (
              <div className="mb-6 p-4 bg-red-500/5 border border-red-500/20 rounded-lg text-red-400">
                {error}
              </div>
            )}

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-primary mb-2">
                  {t.fullName}
                </label>
                {editing ? (
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-blackC border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50"
                    dir={language === 'ar' ? 'rtl' : 'ltr'}
                  />
                ) : (
                  <p className="text-cream">{user.name || '-'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2">
                  {t.email}
                </label>
                {editing ? (
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-blackC border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50"
                    dir="ltr"
                  />
                ) : (
                  <p className="text-cream">{user.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2">
                  {t.phone}
                </label>
                {editing ? (
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-blackC border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50"
                    dir="ltr"
                  />
                ) : (
                  <p className="text-cream">{user.phone || '-'}</p>
                )}
              </div>

              <div className="flex gap-4 pt-4">
                {editing ? (
                  <>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="btn-primary px-6 py-3"
                    >
                      {saving ? t.loading : t.save}
                    </button>
                    <button
                      onClick={() => {
                        setEditing(false);
                        setFormData({
                          name: user.name || '',
                          email: user.email || '',
                          phone: user.phone || ''
                        });
                        setError('');
                      }}
                      className="px-6 py-3 border border-gray-dark text-cream rounded-lg hover:bg-charcoal transition-colors"
                    >
                      {t.cancel}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setEditing(true)}
                    className="btn-primary px-6 py-3"
                  >
                    {t.updateProfile}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="space-y-4">
            {bookings.length === 0 ? (
              <div className="bg-dark border border-gray-dark rounded-lg p-12 text-center">
                <svg className="w-16 h-16 text-[#4a4a4a] mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-gray-light text-lg">{t.noBookings}</p>
                <a href="/book" className="inline-block mt-4 btn-primary">
                  {t.bookRide}
                </a>
              </div>
            ) : (
              bookings.map((booking) => (
                <div key={booking._id} className="bg-dark border border-gray-dark rounded-lg p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-medium text-cream mb-2">
                            {booking.pickupLocation} → {booking.dropoffLocation}
                          </h3>
                          <div className="grid grid-cols-2 gap-4 text-sm text-gray-silver">
                            <div>
                              <span className="text-gray-light">{language === 'en' ? 'Date:' : 'التاريخ:'}</span>{' '}
                              {formatDate(booking.date)}
                            </div>
                            <div>
                              <span className="text-gray-light">{language === 'en' ? 'Time:' : 'الوقت:'}</span>{' '}
                              {formatTime(booking.time)}
                            </div>
                            <div>
                              <span className="text-gray-light">{language === 'en' ? 'Car Type:' : 'نوع السيارة:'}</span>{' '}
                              {booking.carType}
                            </div>
                            <div>
                              <span className="text-gray-light">{language === 'en' ? 'Total:' : 'الإجمالي:'}</span>{' '}
                              <span className="text-primary font-medium">{booking.totalCost} EGP</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-4 py-2 rounded-full text-xs font-medium ${booking.status === 'Confirmed' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/30' :
                        booking.status === 'Pending' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30' :
                          booking.status === 'Cancelled' ? 'bg-red-500/10 text-red-400 border border-red-500/30' :
                            'bg-green-500/10 text-green-400 border border-green-500/30'
                        }`}>
                        {booking.status === 'Confirmed' ? t.confirmed :
                          booking.status === 'Pending' ? t.pending :
                            booking.status === 'Cancelled' ? t.cancelled :
                              booking.status === 'Completed' ? t.completed : booking.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="space-y-4">
            {reviews.length === 0 ? (
              <div className="bg-dark border border-gray-dark rounded-lg p-12 text-center">
                <svg className="w-16 h-16 text-[#4a4a4a] mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
                <p className="text-gray-light text-lg">{t.noReviews}</p>
                <a href="/reviews" className="inline-block mt-4 btn-primary">
                  {t.addReview}
                </a>
              </div>
            ) : (
              reviews.map((review) => (
                <div key={review._id} className="bg-dark border border-gray-dark rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-medium text-cream">{review.customerName}</h3>
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <svg
                              key={i}
                              className={`w-4 h-4 ${i < review.rating ? 'text-primary' : 'text-[#4a4a4a]'}`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                        </div>
                        {review.isApproved && (
                          <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded-full border border-green-500/30">
                            {language === 'en' ? 'Approved' : 'موافق عليه'}
                          </span>
                        )}
                      </div>
                      {review.comment && (
                        <p className="text-gray-silver mb-2">{review.comment}</p>
                      )}
                      <p className="text-xs text-[#4a4a4a]">{formatDate(review.createdAt)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;

